<template>
  <div class="product-details-info">
    <div class="product-details-info__title">
      {{ $t('PRODUCT INFO') }}
    </div>
    <div class="product-details-info__accordions">
      <CustomAccordion
        :title="$t('Technical specifications')"
        class="product-details-info__accordions__accordion"
      >
        <div
          class="product-details-info__accordions__accordion__specification"
          v-for="spec in getSpecifications()"
        >
          <div
            class="product-details-info__accordions__accordion__specification__label"
          >
            {{ $t(spec[0]) }}
          </div>
          <a
            :href="spec[1]"
            v-if="spec[2]"
            class="product-details-info__accordions__accordion__specification__value"
          >
            {{ $t('View video') }}
          </a>
          <div
            v-else
            class="product-details-info__accordions__accordion__specification__value"
          >
            {{ $t(spec[1]) }}
          </div>
        </div>
        <div
          v-if="product.sdc_led_compatible"
          class="product-details-info__accordions__accordion__specification"
        >
          <div
            class="product-details-info__accordions__accordion__specification__led-compatible"
          >
            {{ $t('*You can use LED and energy-saving light bulbs') }}
          </div>
        </div>
      </CustomAccordion>
      <CustomAccordion
        :title="$t('Product options')"
        class="product-details-info__accordions__accordion"
      >
        <div
          class="product-details-info__accordions__accordion__option"
          v-for="option in product.configurable_options"
        >
          <div
            class="product-details-info__accordions__accordion__option__label"
          >
            {{ $t(option.label) }}
          </div>
          <div
            class="product-details-info__accordions__accordion__option__values"
          >
            {{ $t(getOptionValues(option)) }}
          </div>
        </div>
      </CustomAccordion>
      <CustomAccordion
        v-if="attachments.length > 0"
        :title="$t('Downloads')"
        class="product-details-info__accordions__accordion"
      >
        <div
          class="product-details-info__accordions__accordion__attachment"
          v-for="attachment in attachments"
        >
          <IconFileBlack />
          <CustomCta type="label" :link="attachment.url">
            {{ attachment.name }}
          </CustomCta>
        </div>
      </CustomAccordion>
    </div>
  </div>
</template>

<script>
import { ref, defineComponent } from '@nuxtjs/composition-api';
import { CustomCta, CustomAccordion } from '~/components/General';
import { IconFileBlack } from '~/components/General/Icons';
import dataManipulation from '~/helpers/dataManipulation';

export default defineComponent({
  name: 'ProductDetailsInfo',
  components: {
    IconFileBlack,
    CustomCta,
    CustomAccordion,
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const attachments = ref([]);
    const { stringIsUUID, beautifyAttributeName, isValidUrl } =
      dataManipulation();

    const getSpecifications = () => {
      const specsArray = [];
      for (const spec of Object.entries(props.product)) {
        if (
          spec[0].includes('sdc') &&
          !spec[0].includes('ship_date') &&
          spec[1]
        ) {
          switch (typeof spec[1]) {
            case 'string': {
              if (!stringIsUUID(spec[1])) {
                specsArray.push([
                  beautifyAttributeName(spec[0]),
                  spec[1],
                  isValidUrl(spec[1]),
                ]);
              }
              break;
            }
            case 'boolean': {
              specsArray.push([
                beautifyAttributeName(spec[0]),
                spec[1] ? 'Yes' : 'No',
              ]);
              break;
            }
            case 'object': {
              specsArray.push([beautifyAttributeName(spec[0]), spec[1].label]);
              break;
            }
            default: {
              break;
            }
          }
        }
      }
      return specsArray;
    };

    const getOptionValues = (option) => {
      let values = '';
      option.values.forEach((value, index) => {
        if (index !== 0) {
          values += ', ';
        }
        values += value.label;
      });
      return values;
    };

    return {
      attachments,
      getSpecifications,
      getOptionValues,
      isValidUrl,
    };
  },
});
</script>

<style lang="scss" scoped>
.product-details-info {
  &__title {
    @include label-s;
    padding-bottom: 1.25rem;
  }
  &__accordions {
    &__accordion {
      &__specification {
        min-height: 3.75rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-top: var(--general-border);
        border-color: var(--c-light-gray);
        &__label {
          padding: 1rem 0.625rem 1rem 0;
          @include desktop-h7;
        }
        &__value {
          padding: 1rem 0;
          max-width: 80%;
          @include paragraph-m;
        }
        &__led-compatible {
          padding: 1rem 0;
          @include paragraph-m;
          color: var(--c-dark-gray);
        }
      }
      &__option {
        padding-bottom: 1.875rem;
        &:last-child {
          padding-bottom: 1.25rem;
        }
        &__label {
          @include desktop-h7;
          padding-bottom: 0.3125rem;
        }
        &__values {
          @include paragraph-m;
        }
      }
      &__attachment {
        display: flex;
        align-items: center;
        &:last-child {
          padding-bottom: 1.25rem;
        }
      }
    }
  }
}
</style>
